import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useTenantBranding } from "@/hooks/useTenantBranding";

interface ThemeContextType {
  branding: any;
  isCustomThemed: boolean;
  refreshBranding: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a DynamicThemeProvider");
  }
  return context;
}

interface DynamicThemeProviderProps {
  children: ReactNode;
}

export function DynamicThemeProvider({ children }: DynamicThemeProviderProps) {
  const [currentBranding, setCurrentBranding] = useState<any>(null);
  const [isCustomThemed, setIsCustomThemed] = useState(false);
  const { getBrandingByDomain } = useTenantBranding();

  const detectTenantFromDomain = async () => {
    const hostname = window.location.hostname;
    
    // Skip for localhost and default domains
    if (hostname === 'localhost' || hostname.includes('lovable.app')) {
      return;
    }

    try {
      const branding = await getBrandingByDomain(hostname);
      if (branding && branding.is_active) {
        setCurrentBranding(branding);
        setIsCustomThemed(true);
        applyCustomTheme(branding);
      } else {
        // No branding found - use default theme
        setCurrentBranding(null);
        setIsCustomThemed(false);
      }
    } catch (error) {
      console.error('Error detecting tenant from domain:', error);
      // Silently fall back to default theme
      setCurrentBranding(null);
      setIsCustomThemed(false);
    }
  };

  const applyCustomTheme = (branding: any) => {
    const root = document.documentElement;
    
    if (branding.primary_color) {
      // Convert hex to HSL for CSS variables
      const hsl = hexToHsl(branding.primary_color);
      root.style.setProperty('--primary', hsl);
    }
    
    if (branding.secondary_color) {
      const hsl = hexToHsl(branding.secondary_color);
      root.style.setProperty('--secondary', hsl);
    }
    
    if (branding.background_color) {
      const hsl = hexToHsl(branding.background_color);
      root.style.setProperty('--background', hsl);
    }
    
    if (branding.text_color) {
      const hsl = hexToHsl(branding.text_color);
      root.style.setProperty('--foreground', hsl);
    }

    // Apply custom CSS if provided
    if (branding.custom_css) {
      const styleElement = document.getElementById('custom-tenant-styles') || document.createElement('style');
      styleElement.id = 'custom-tenant-styles';
      styleElement.textContent = branding.custom_css;
      document.head.appendChild(styleElement);
    }

    // Update favicon if provided
    if (branding.favicon_url) {
      updateFavicon(branding.favicon_url);
    }

    // Update page title if app_name is provided
    if (branding.app_name) {
      document.title = branding.app_name;
    }
  };

  const hexToHsl = (hex: string): string => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert hex to RGB
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  const updateFavicon = (faviconUrl: string) => {
    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = faviconUrl;
    document.getElementsByTagName('head')[0].appendChild(link);
  };

  const refreshBranding = () => {
    detectTenantFromDomain();
  };

  useEffect(() => {
    detectTenantFromDomain();
  }, []);

  return (
    <ThemeContext.Provider value={{
      branding: currentBranding,
      isCustomThemed,
      refreshBranding
    }}>
      {children}
    </ThemeContext.Provider>
  );
}